import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { coneApi, operatorsApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import ReasonPicker from '../../../shared/components/ReasonPicker'
import {
  StepBar, FlowNote, InfoCard, BigButton, ScanZone, QRBox,
  SuccessScreen, VarianceAlert, SectionHeading, KVRow, Divider, LoadingSpinner,
} from '../../../shared/components/UI'
import { clsx } from 'clsx'

const HANDOFF_STEPS     = ['Scan Container', 'Select Operator', 'Handoff QR']
const COMPLETION_STEPS  = ['Scan QR', 'Enter Data', 'Master QR']

const SHORT_REASONS = [
  'Thread breakage during conning',
  'Machine jam — material lost',
  'Rejected cones removed',
  'Operator handling loss',
  'Other (specify below)',
]

export default function ConePage() {
  const { user } = useAuthStore()
  const navigate  = useNavigate()

  // Which sub-flow is active
  const [mode, setMode]         = useState('choose') // 'choose' | 'handoff' | 'completion'

  // ── Handoff state ──
  const [hStep, setHStep]       = useState(0)
  const [containerInput, setContainerInput] = useState('')
  const [boiledContext, setBoiledContext] = useState(null)
  const [loadingBoiled, setLoadingBoiled] = useState(false)
  const [operators, setOperators] = useState([])
  const [operator, setOperator] = useState(null)
  const [handoffData, setHandoffData] = useState(null)

  // ── Completion state ──
  const [cStep, setCStep]       = useState(0)
  const [opQrInput, setOpQrInput] = useState('')
  const [returnContext, setReturnContext] = useState(null)
  const [loadingReturn, setLoadingReturn] = useState(false)

  const [jariRolls, setJariRolls] = useState('')
  const [returnWeight, setReturnWeight] = useState('')
  const [shortReason, setShortReason]   = useState('')
  const [shortNote, setShortNote]       = useState('')
  const [completionResult, setCompletionResult] = useState(null)
  
  const [error, setError] = useState('')

  useEffect(() => {
    if (mode === 'handoff') {
      operatorsApi.list({ role: 'operator' }).then(res => {
        setOperators(res.data.data)
      })
    }
  }, [mode])

  // ── Handoff Logic ──
  const handleScanBoiled = () => {
    if (!containerInput.trim()) return
    setLoadingBoiled(true)
    setError('')
    coneApi.scanBoiled(containerInput.trim())
      .then(res => {
        setBoiledContext(res.data.data)
        setHStep(1)
      })
      .catch(err => setError(err.response?.data?.message || 'No boiled container found'))
      .finally(() => setLoadingBoiled(false))
  }

  const { mutate: handoffMutate, isPending: hPending } = useMutation({
    mutationFn: () => coneApi.handoff({ 
      boilingLogId: boiledContext.id, 
      containerId: boiledContext.container_id, 
      operatorId: operator?.id,
      weightHandedKg: boiledContext.spindleOutput?.actual_output_kg
    }),
    onSuccess:  (res) => {
      setHandoffData(res.data.data)
      setHStep(2)
    },
    onError: (err) => setError(err.response?.data?.message || 'Handoff failed')
  })

  // ── Completion Logic ──
  const handleScanReturn = () => {
    if (!opQrInput.trim()) return
    setLoadingReturn(true)
    setError('')
    coneApi.scanReturn(opQrInput.trim())
      .then(res => {
        setReturnContext(res.data.data)
        setCStep(1)
      })
      .catch(err => setError(err.response?.data?.message || 'Operator QR not found'))
      .finally(() => setLoadingReturn(false))
  }

  const { mutate: returnMutate, isPending: rPending } = useMutation({
    mutationFn: (clarification = {}) => coneApi.submitReturn({
      coneHandoffId: returnContext.id,
      jariRollCount: parseInt(jariRolls),
      returnWeightKg: parseFloat(returnWeight),
      ...clarification
    }),
    onSuccess: (res) => {
      setCompletionResult(res.data.data)
      setCStep(3)
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to submit return')
  })

  const handleReturnSave = () => {
    if (!jariRolls || !returnWeight || parseFloat(returnWeight) <= 0) return
    const givenWeight = parseFloat(returnContext.weight_handed_kg)
    const retW = parseFloat(returnWeight)
    const variance = ((retW - givenWeight) / givenWeight * 100)
    
    if (variance < -2 && !shortReason) { 
      setCStep('clarify')
    } else {
      returnMutate({ clarificationReason: shortReason, clarificationNote: shortNote })
    }
  }

  // ── UI Computations ──
  const GIVEN_W = returnContext ? parseFloat(returnContext.weight_handed_kg) : 0
  const RET_W   = parseFloat(returnWeight) || 0
  const DIFF    = (GIVEN_W - RET_W).toFixed(2)
  const PCT     = GIVEN_W > 0 ? (((RET_W - GIVEN_W) / GIVEN_W) * 100).toFixed(1) : 0
  const IS_SHORT = RET_W > 0 && parseFloat(PCT) < -2

  // ── CHOOSE ──
  if (mode === 'choose') return (
    <TabletScreen>
      <TabletHeader title="Cone Formation" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4 pt-6">
        <SectionHeading>What do you want to do?</SectionHeading>
        <div className="flex flex-col gap-4 mt-4">
          <button
            onClick={() => { setMode('handoff'); setHStep(0); setError('') }}
            className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-teal-500 hover:bg-teal-50 transition-colors shadow-sm"
          >
            <p className="font-semibold text-gray-800 mb-1">Cone Handoff</p>
            <p className="text-sm text-gray-500">Scan boiled container → select operator → give handoff QR</p>
          </button>
          <button
            onClick={() => { setMode('completion'); setCStep(0); setError('') }}
            className="bg-white border border-gray-200 rounded-2xl p-5 text-left hover:border-teal-500 hover:bg-teal-50 transition-colors shadow-sm"
          >
            <p className="font-semibold text-gray-800 mb-1">Cone Completion</p>
            <p className="text-sm text-gray-500">Scan operator return QR → enter final data → close batch</p>
          </button>
        </div>
      </div>
    </TabletScreen>
  )

  // ── HANDOFF FLOW ──
  if (mode === 'handoff') return (
    <TabletScreen>
      <TabletHeader title="Cone Handoff" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">
        <StepBar steps={HANDOFF_STEPS} current={hStep} />

        {hStep === 0 && (
          <>
            <ScanZone teal title="Scan Boiled Container" subtitle="Scan the container that finished boiling" />
            <div className="flex gap-2">
              <input className="form-input flex-1 text-sm" placeholder="Or type container ID" value={containerInput} onChange={(e) => setContainerInput(e.target.value)} />
              <button disabled={loadingBoiled} onClick={handleScanBoiled} className="bg-teal-600 text-white px-4 rounded-lg text-sm font-medium">
                {loadingBoiled ? '...' : 'Load'}
              </button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            <BigButton variant="secondary" onClick={() => setMode('choose')} className="mt-4">Back</BigButton>
          </>
        )}

        {hStep === 1 && (
          <>
            <FlowNote variant="teal">Boiling confirmed · Batch {boiledContext.spindleOutput?.spindleLoading?.batch?.batch_number}</FlowNote>
            <InfoCard rows={[
              { label: 'Container',  value: boiledContext.container_id,    valueVariant: 'blue' },
              { label: 'Batch',      value: boiledContext.spindleOutput?.spindleLoading?.batch?.batch_number },
              { label: 'Shade',      value: boiledContext.spindleOutput?.spindleLoading?.batch?.shade?.name },
              { label: 'Weight',     value: `${boiledContext.spindleOutput?.actual_output_kg} KG` },
            ]} />
            <SectionHeading>Select conning machine operator</SectionHeading>
            <div className="flex flex-col gap-2 mb-5">
              {operators.map((op) => (
                <div key={op.id} onClick={() => setOperator(op)}
                  className={clsx('select-card', operator?.id === op.id && 'select-card-active')}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 bg-brand-100 text-brand-700">
                      {op.name.substring(0,2).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{op.name}</p>
                      <p className="text-xs text-gray-400">Operator · {op.phone || '--'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
            <BigButton variant="success" disabled={!operator || hPending} onClick={() => handoffMutate()}>
              {hPending ? 'Generating...' : 'Generate Handoff QR'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => setHStep(0)}>Back</BigButton>
          </>
        )}

        {hStep === 2 && (
          <>
            <div className="text-center mb-4 mt-2">
              <p className="text-base font-medium text-gray-800 mb-1">Hand this QR to the operator</p>
              <p className="text-sm text-gray-500 mb-5">Operator scans this when returning cones</p>
              <div className="flex justify-center mb-3">
                <QRBox label={handoffData?.handoff_qr_id} sublabel={`Issued: ${new Date().toLocaleTimeString()}`} size={130} />
              </div>
            </div>
            <InfoCard rows={[
              { label: 'Batch',         value: boiledContext.spindleOutput?.spindleLoading?.batch?.batch_number, valueVariant: 'blue' },
              { label: 'Operator',      value: operator?.name },
              { label: 'Weight handed', value: `${boiledContext.spindleOutput?.actual_output_kg} KG` },
            ]} />
            <BigButton variant="primary" onClick={() => navigate('/app/dashboard')}>Done — Back to Dashboard</BigButton>
          </>
        )}
      </div>
    </TabletScreen>
  )

  // ── COMPLETION FLOW ──
  return (
    <TabletScreen>
      <TabletHeader title="Cone Completion" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">
        <StepBar steps={COMPLETION_STEPS} current={cStep === 'clarify' ? 1 : (cStep > 2 ? 2 : cStep)} />

        {/* Scan operator return QR */}
        {cStep === 0 && (
          <>
            <ScanZone teal title="Scan Operator QR" subtitle="Scan the QR the operator brings back" />
            <div className="flex gap-2">
              <input className="form-input flex-1 text-sm" placeholder="Or type QR ID" value={opQrInput} onChange={(e) => setOpQrInput(e.target.value)} />
              <button disabled={loadingReturn} onClick={handleScanReturn} className="bg-teal-600 text-white px-4 rounded-lg text-sm font-medium">
                {loadingReturn ? '...' : 'Load'}
              </button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            <BigButton variant="secondary" onClick={() => setMode('choose')} className="mt-4">Back</BigButton>
          </>
        )}

        {/* Enter cone data */}
        {cStep === 1 && (
          <>
            <FlowNote variant="teal">QR matched · {returnContext.boilingLog?.spindleOutput?.spindleLoading?.batch?.batch_number} · {returnContext.operator?.name}</FlowNote>
            <InfoCard rows={[
              { label: 'Batch',        value: returnContext.boilingLog?.spindleOutput?.spindleLoading?.batch?.batch_number, valueVariant: 'blue' },
              { label: 'Operator',     value: returnContext.operator?.name },
              { label: 'Weight given', value: `${returnContext.weight_handed_kg} KG` },
            ]} />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="form-label">No. of jari rolls</label>
                <input type="number" inputMode="numeric" className="form-input text-2xl text-center py-3 font-semibold" placeholder="0" value={jariRolls} onChange={(e) => setJariRolls(e.target.value)} />
              </div>
              <div>
                <label className="form-label">Final weight (KG)</label>
                <input type="number" inputMode="decimal" className="form-input text-2xl text-center py-3 font-semibold" placeholder="0.0" step="0.1" value={returnWeight} onChange={(e) => setReturnWeight(e.target.value)} />
              </div>
            </div>
            {RET_W > 0 && (
              <div className={clsx('rounded-xl px-4 py-3 mb-3 text-sm font-medium', IS_SHORT ? 'bg-red-50 text-red-700' : 'bg-teal-50 text-teal-700')}>
                {IS_SHORT
                  ? `Short by ${DIFF} KG (${PCT}%) — clarification required`
                  : 'Weight OK · within acceptable range'}
              </div>
            )}
            {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
            <BigButton variant="success" onClick={handleReturnSave} disabled={!jariRolls || !returnWeight || rPending}>
              {rPending ? 'Saving…' : 'Save & Continue'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => setCStep(0)}>Back</BigButton>
          </>
        )}

        {/* Clarification */}
        {cStep === 'clarify' && (
          <>
            <VarianceAlert pct={`${PCT}%`} expectedKg={GIVEN_W} actualKg={RET_W}>
              <div />
            </VarianceAlert>
            <div className="flow-note-amber mb-4">Admin has been notified. Please provide a reason.</div>
            <SectionHeading>Reason for short weight</SectionHeading>
            <div className="mb-4">
              <ReasonPicker reasons={SHORT_REASONS} onChange={setShortReason} />
            </div>
            <div className="mb-4">
              <label className="form-label">Additional notes (optional)</label>
              <textarea className="form-input" rows={3} placeholder="Describe what happened…" value={shortNote} onChange={(e) => setShortNote(e.target.value)} />
            </div>
            {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
            <BigButton variant="danger" disabled={!shortReason || rPending} onClick={handleReturnSave}>
              {rPending ? 'Saving...' : 'Submit & Generate Master QR'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => setCStep(1)}>Back</BigButton>
          </>
        )}

        {/* Master QR */}
        {cStep === 3 && completionResult && (
          <>
            <div className="text-center mb-4 mt-2">
              <p className="text-sm font-medium text-gray-800 mb-1">Place this QR on the bori</p>
              <p className="text-xs text-gray-500 mb-4">Contains full batch history · scan anytime to trace</p>
              <div className="flex justify-center mb-2">
                <QRBox label={completionResult?.master_qr_id} sublabel={new Date().toLocaleString()} size={140} />
              </div>
            </div>
            <SectionHeading className="mt-4">Final batch details</SectionHeading>
            <div className="info-card">
              <KVRow label="Batch"         value={returnContext?.boilingLog?.spindleOutput?.spindleLoading?.batch?.batch_number} />
              <KVRow label="Shade"         value={returnContext?.boilingLog?.spindleOutput?.spindleLoading?.batch?.shade?.name} />
              <KVRow label="Final rolls"   value={`${completionResult?.jari_roll_count} rolls`} />
              <KVRow label="Final weight"  value={`${completionResult?.return_weight_kg} KG`} valueClass="text-teal-600 font-bold" />
              <KVRow label="Status"        value="CLOSED" valueClass="text-green-600 font-bold" />
            </div>
            <BigButton variant="success" onClick={() => navigate('/app/dashboard')}>Close & Back to Dashboard</BigButton>
          </>
        )}
      </div>
    </TabletScreen>
  )
}
